/**
 * @file ImportTypeDropdown.js
 * @description A dropdown component for selecting an import type.
 */

import { useState, useEffect } from 'react';
import { getImportTypes } from '../../../services/importService';
import { Dropdown } from '../../helpers/Dropdown';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import Toast from '../../ui/toasts/Toast';
import { getText } from '../../../i18n';

const ImportTypeDropdown = ({
    className,
    inlineLabel = true,
    selectedType,
    onTypeChange,
    required = false,
    disabled = false,
}) => {
    const [types, setTypes] = useState([]);
    const [defaultLabel, setDefaultLabel] = useState('Select Category');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchImportTypes = async () => {
            try {
                const data = await getImportTypes();
                setTypes(data);

                const matchingType =
                    selectedType &&
                    data.find(type => type.id === selectedType.id);
                setDefaultLabel(
                    matchingType ? matchingType.name : 'Select Category',
                );
            } catch (error) {
                console.error('Could not fetch import categories:', error);
                Toast.error(getText('msg_cd35c70bf240'));
            } finally {
                setIsLoading(false);
            }
        };

        fetchImportTypes();
    }, []);

    useEffect(() => {
        if (selectedType && types.length > 0) {
            const matchingType = types.find(
                type => type.id === selectedType.id,
            );
            setDefaultLabel(
                matchingType ? matchingType.name : 'Select Category',
            );
        } else {
            setDefaultLabel('Select Category');
        }
    }, [selectedType, types]);

    const typeOptions = [
        { value: null, label: getText('msg_4d18568c50da') },
        ...types.map(type => ({
            value: { id: type.id, name: type.name },
            label: type.name,
        })),
    ];

    return (
        <div
            className={`form-group mb-3 ${
                inlineLabel ? 'inline-input' : ''
            } ${className}`}
        >
            <LoadingWrapper isLoading={isLoading}>
                <Dropdown
                    defaultValue={defaultLabel}
                    options={typeOptions}
                    onChange={value => onTypeChange(value)}
                    value={selectedType}
                    disabled={types.length === 0 || isLoading || disabled}
                />
            </LoadingWrapper>
        </div>
    );
};

export default ImportTypeDropdown;
